import { Injectable, signal } from '@angular/core';
import { Subscription, from, of, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap, tap, toArray } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { GeneralInnovationActivityResultsService } from './general-innovation-activity-results.service';

export type HistoryJobStatus = 'running' | 'done' | 'error';

export interface HistoryDownloadJob {
  id: number;
  themeName: string;
  total: number;
  done: number;
  stage: string;
  status: HistoryJobStatus;
  skipped: (number | null)[];
}

export const PARALLEL_YEARS = 2;
const DONE_DISMISS_MS = 6000;

/**
 * Yearly top-companies history downloads, held at app-root scope so they keep running
 * while the user navigates; progress is shown by HistoryDownloadTrayComponent.
 * Each year is computed (and cached) separately, retried once, and skipped if it still fails.
 */
@Injectable({ providedIn: 'root' })
export class HistoryDownloadService {
  readonly jobs = signal<HistoryDownloadJob[]>([]);
  readonly minimized = signal(false);

  private subs = new Map<number, Subscription>();
  private nextId = 1;

  constructor(private api: GeneralInnovationActivityResultsService) {}

  start(query: string, themeName: string | null, years: (number | null)[]): void {
    const id = this.nextId++;
    const name = themeName || 'Theme';
    this.jobs.update(jobs => [...jobs, {
      id, themeName: name, total: years.length, done: 0,
      stage: 'Computing reference years…', status: 'running', skipped: [],
    }]);
    this.minimized.set(false);

    const sub = from(years).pipe(
      mergeMap(year => this.api.prepareTopCompaniesHistory(query, year).pipe(
        retry({ count: 1, delay: () => timer(2000) }),
        map(() => ({ year, ok: true })),
        catchError(() => of({ year, ok: false })),
        tap(result => this.patch(id, j => ({
          done: j.done + 1,
          stage: `${j.done + 1} of ${j.total} years computed`,
          skipped: result.ok ? j.skipped : [...j.skipped, year],
        }))),
      ), PARALLEL_YEARS),
      toArray(),
      switchMap(results => {
        const ready = years.filter(y => results.some(r => r.year === y && r.ok));
        if (!ready.length) return throwError(() => new Error('All years failed'));
        this.patch(id, () => ({ stage: 'Building Excel file…' }));
        return this.api.downloadTopCompaniesHistory(query, ready);
      }),
    ).subscribe({
      next: res => {
        if (res.status === 204 || !res.body?.size) {
          this.fail(id, 'No public companies found for these years.');
          return;
        }
        const fileName = name.substring(0, 40).replace(/[^\w\- ]+/g, '');
        saveAs(res.body, `${fileName}_public_companies_history.xlsx`);
        this.patch(id, () => ({ status: 'done', stage: 'Downloaded' }));
        setTimeout(() => this.remove(id), DONE_DISMISS_MS);
      },
      error: () => this.fail(id, 'Download failed. Try again, computed years are cached.'),
    });
    this.subs.set(id, sub);
  }

  /** Cancels a running job (if any) and removes it from the tray. */
  remove(id: number): void {
    this.subs.get(id)?.unsubscribe();
    this.subs.delete(id);
    this.jobs.update(jobs => jobs.filter(j => j.id !== id));
  }

  private fail(id: number, message: string): void {
    this.patch(id, () => ({ status: 'error', stage: message }));
  }

  private patch(id: number, change: (job: HistoryDownloadJob) => Partial<HistoryDownloadJob>): void {
    this.jobs.update(jobs => jobs.map(j => j.id === id ? { ...j, ...change(j) } : j));
  }
}
